import React, { ReactNode } from 'react';
import { Field, useField } from 'formik';
import { Box, FormControlLabel, TextField } from '@mui/material';
import { theme } from 'theme';

export const styles = {
  labelRoot: {
    marginLeft: 0,
    marginTop: 0,
    marginRight: 0,
    width: '100%',
  },
  label: {
    display: 'flex',
  },
  helperText: {
    fontWeight: 'normal',
    textAlign: 'right',
  },
  labelPlacementTop: {
    alignItems: 'flex-start',
  },
  textInput: {
    marginTop: theme.spacing(0.5),
    width: '100%',
    '& .MuiFilledInput-input': {
      paddingLeft: theme.spacing(1.5),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      width: '100%',
    },
    '& .MuiFilledInput-multiline.MuiFilledInput-marginDense': {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
};

interface TextFieldProps {
  label: string;
  name: string;
  maxChar: number;
  readOnly?: boolean;
  disabled?: boolean;
  customValue?: string;
  customError?: string;
  placeholder?: string;
  showCharacterCount?: boolean;
  rowCount?: number;
  propFormikContext?: Record<string, unknown>;
  customId?: string;
  customInputProps?: Record<string, unknown>;
  customType?: string;
  multiline?: boolean;
  labelComponent?: ReactNode;
  gridSize?: number;
}

export const ValidatedTextField: React.FC<TextFieldProps> = (rawProps) => {
  const props = {
    showCharacterCount: false,
    rowCount: 1,
    ...rawProps,
  };
  const { label } = props;
  const [field, meta] = useField<string>(props);
  const value = props.customValue ?? field.value ?? '';
  let errorText = meta.error && meta.touched ? meta.error : '';
  if (props.customError) {
    errorText = props.customError;
  }

  return (
    <FormControlLabel
      sx={{
        margin: 0,
        width: '100%',
        display: 'flex',
        '& .MuiFormControlLabel-label': {
          width: '100%',
        },
      }}
      label={
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-start',
          }}
        >
          {label} {props.labelComponent}
        </Box>
      }
      labelPlacement="top"
      disabled={props.disabled}
      control={
        <Field
          variant="filled"
          margin="dense"
          sx={styles.textInput}
          rows={props.rowCount}
          type={props.customType ?? 'text'}
          multiline={props.rowCount !== 1 || props.multiline}
          id={props.customId ?? `${props.name}-text-input`}
          as={TextField}
          helperText={
            errorText ||
            (props.showCharacterCount &&
              (!value ? `0/${props.maxChar}` : `${value.toLocaleString().length}/${props.maxChar}`))
          }
          errortext={errorText}
          FormHelperTextProps={{
            className: props.showCharacterCount && !errorText ? styles.helperText : '',
          }}
          inputProps={{
            maxLength: props.maxChar,
            placeholder: props.placeholder,
            readOnly: props.readOnly,
            disabled: props.disabled,
            'data-testid': props.customId ?? `${props.name}-text-input`,
          }}
          InputProps={props.customInputProps ?? { disableUnderline: true }}
          {...field}
          value={value}
          fullWidth
          error={!!errorText}
        />
      }
    />
  );
};
